/** The CDN the widget preloads its own logo from. */
const ASSET_HOST = 'file-cdn.superchat.de';

/**
 * Stop after this many. The vendor inserts one; anything beyond a handful means
 * their SDK has started re-inserting and we should stop rather than fight it.
 */
const MAX_REMOVALS = 4;

/** And stop regardless, so nothing observes for the life of the tab. */
const LIFETIME_MS = 30_000;

/**
 * Removes the preload the Superchat SDK inserts for its own logo.
 *
 * The SDK preloads that image through React's resource API, but only renders it
 * after fetching its workspace configuration — a round trip that finishes well
 * outside the roughly three seconds Chrome allows before it warns that a
 * preloaded resource went unused. So every page carrying the widget wrote a
 * `The resource … was preloaded using link preload but not used …` line.
 *
 * Being honest about what this is: a workaround for someone else's SDK, and it
 * buys a console line and nothing else. It was measured against the deployed app
 * and saves no bytes and no time — cross-origin timing is redacted, and the load
 * event moved further from run to run than it did between the two cases. The
 * reason to keep it is that this app now asks operators to read the console for
 * `[CSP]` refusals, and a permanent warning is what teaches them to stop.
 *
 * Scoped as tightly as the job allows, because it reaches into a third party's
 * DOM: the head only rather than the document, `addedNodes` rather than a query
 * per mutation, a cap, a deadline, and a caller that ties it to the widget's own
 * lifetime. Measured on the deployed app it saw ten callbacks and made one
 * removal — the SDK inserts the link once and does not put it back.
 *
 * Deliberately one host rather than a configurable list. Turning it into one
 * would take five minutes whenever a second case appears, and until then a list
 * would advertise this as the way to deal with a noisy dependency — when four of
 * the five warnings this app carried were removed at their source instead, by
 * naming two missing addresses in the policy, upgrading a dependency and setting
 * an option on a viewer. This is the exception because there was no source we
 * control.
 *
 * So if a second vendor does need it, generalise then, and check first what was
 * checked here: that the link is inserted once, into the head, and not put back
 * when it is removed, and that nothing in their SDK reads it afterwards. The cap
 * and the deadline below limit the damage when that turns out to be wrong, but
 * they are a backstop, not a substitute for looking.
 *
 * Returns its own teardown. Safe to call where there is no document.
 */
export const dropVendorPreload = (documentRef: Document = document): (() => void) => {
	const head = documentRef.head;
	if (!head || typeof MutationObserver === 'undefined') {
		return () => undefined;
	}

	let removals = 0;

	const isVendorPreload = (node: Node): node is Element =>
		node.nodeName === 'LINK' &&
		(node as Element).getAttribute('rel') === 'preload' &&
		((node as Element).getAttribute('href') || '').includes(ASSET_HOST);

	const observer = new MutationObserver((records) => {
		records.forEach((record) => {
			record.addedNodes.forEach((node) => {
				if (removals >= MAX_REMOVALS || !isVendorPreload(node)) {
					return;
				}
				node.remove();
				removals += 1;
				if (removals >= MAX_REMOVALS) {
					observer.disconnect();
				}
			});
		});
	});

	observer.observe(head, { childList: true });
	const deadline = setTimeout(() => observer.disconnect(), LIFETIME_MS);

	return () => {
		clearTimeout(deadline);
		observer.disconnect();
	};
};
